import React from 'react';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LINE_ITEM_DAY,
  LINE_ITEM_FIXED,
  LINE_ITEM_HOUR,
  LINE_ITEM_ITEM,
  LINE_ITEM_NIGHT,
  LINE_ITEM_OFFER,
  LINE_ITEM_REQUEST,
  propTypes,
} from '../../util/types';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

const toNumericValue = value => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (value && typeof value.toNumber === 'function') {
    const converted = value.toNumber();
    return Number.isFinite(converted) ? converted : 0;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

/**
 * A component that renders the base price as a line item.
 *
 * @component
 * @param {Object} props
 * @param {Array<propTypes.lineItem>} props.lineItems - The line items to render
 * @param {propTypes.lineItemUnitType} props.code - The code of the line item
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const LineItemBasePriceMaybe = props => {
  const { lineItems, code, intl, itemLabel } = props;
  const isNightly = code === LINE_ITEM_NIGHT;
  const isDaily = code === LINE_ITEM_DAY;
  const isHourly = code === LINE_ITEM_HOUR;
  const isFixed = code === LINE_ITEM_FIXED;
  const isRequest = code === LINE_ITEM_REQUEST;
  const isOffer = code === LINE_ITEM_OFFER;
  const translationKey = isNightly
    ? 'OrderBreakdown.baseUnitNight'
    : isDaily
    ? 'OrderBreakdown.baseUnitDay'
    : isHourly
    ? 'OrderBreakdown.baseUnitHour'
    : isFixed
    ? 'OrderBreakdown.baseUnitFixedBooking'
    : isRequest
    ? 'OrderBreakdown.baseUnitRequest'
    : isOffer
    ? 'OrderBreakdown.baseUnitOffer'
    : 'OrderBreakdown.baseUnitQuantity';

  // Find correct line-item for given code prop.
  // It should be one of the following: 'line-item/night, 'line-item/day', 'line-item/hour', 'line-item/fixed', 'line-item/item', 'line-item/offer', 'line-item/request'
  // These are defined in '../../util/types';
  const unitPurchases = lineItems.filter(item => item.code === code && !item.reversal);
  const unitPurchase = unitPurchases[0];
  const hasMultipleUnitPurchases = unitPurchases.length > 1;
  const quantity = hasMultipleUnitPurchases
    ? unitPurchases.reduce((sum, item) => {
        return sum + toNumericValue(item?.units ?? item?.quantity);
      }, 0)
    : unitPurchase?.units
    ? toNumericValue(unitPurchase.units)
    : unitPurchase?.quantity
    ? toNumericValue(unitPurchase.quantity)
    : null;
  const hasUniformUnitPrice =
    unitPurchases.length > 0 &&
    unitPurchases.every(item => {
      return (
        item?.unitPrice?.amount === unitPurchase?.unitPrice?.amount &&
        item?.unitPrice?.currency === unitPurchase?.unitPrice?.currency
      );
    });
  const aggregatedTotalMoney =
    hasMultipleUnitPurchases && unitPurchase?.lineTotal?.currency
      ? new Money(
          unitPurchases.reduce((sum, item) => sum + (item?.lineTotal?.amount || 0), 0),
          unitPurchase.lineTotal.currency
        )
      : null;
  const unitPrice =
    hasMultipleUnitPurchases && !hasUniformUnitPrice
      ? null
      : unitPurchase
      ? formatMoney(intl, unitPurchase.unitPrice)
      : null;
  const total = unitPurchase
    ? formatMoney(intl, aggregatedTotalMoney || unitPurchase.lineTotal)
    : null;

  const customItemLabel =
    code === LINE_ITEM_ITEM &&
    itemLabel?.type === 'count' &&
    Number.isInteger(itemLabel?.quantity) ? (
      <FormattedMessage
        id="OrderBreakdown.baseUnitMultipleItems"
        values={{ quantity: itemLabel.quantity }}
      />
    ) : code === LINE_ITEM_ITEM && itemLabel?.type === 'title' && itemLabel?.text ? (
      itemLabel.text
    ) : null;

  const message =
    customItemLabel ? (
      customItemLabel
    ) : hasMultipleUnitPurchases && code === LINE_ITEM_ITEM && !hasUniformUnitPrice ? (
      <FormattedMessage id="OrderBreakdown.baseUnitMultipleItems" values={{ quantity }} />
    ) : unitPurchase?.seats ? (
    <FormattedMessage
      id={`${translationKey}Seats`}
      values={{ unitPrice, quantity, seats: unitPurchase.seats }}
    />
  ) : (
    <FormattedMessage id={translationKey} values={{ unitPrice, quantity }} />
  );

  return quantity && total ? (
    <div className={css.lineItem}>
      <span className={css.itemLabel}>{message}</span>
      <span className={css.itemValue}>{total}</span>
    </div>
  ) : null;
};

export default LineItemBasePriceMaybe;
